import React, { useEffect, useMemo, useState } from "react";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { useNavigate } from "react-router";

import { FormatDateTime } from "../../../../../utils/functions/ResourceFunctions";
import { useSearchFilter } from "../../../../../utils/functions/useDate";
import {
  useGetHODScoreRecommwndationQuery,
  useGetHrDepartmentsQuery,
  useGetManagementAppraisalQuery,
  useManagementCommentMutation,
} from "../../../../api/hrApi";
import DashboardStyle from "../../../../dashboard/Styles/Dashboard.module.css";
import PageStyle from "../../../Components/Layout/PageLayout";
import { TableActions } from "../../../Components/Misc/Actions";
import Pagination from "../../../Components/Pagination/Pagination";
import {
  CalendarFilter2,
  ProDropFilter,
  SearchFilter,
} from "../../../Components/Search/Search";
import Table from "../../../Components/Table/Table";
import { GetLocalStorage } from "../../../../../utils/functions/GetLocalStorage";
import ApproveModal from "../../HR/Appraisal/Modals/ApproveModal";
import toast from "react-hot-toast";
import "react-datepicker/dist/react-datepicker.css";

const ManagementAppraisal = () => {
  const {
    searchText,
    filter,
    setSearchText,
    setFilter,
    pageSize,
    setPageSize,
    currentPage,
    setCurrentPage,
  } = useSearchFilter({
    initialFilter: 1,
  });
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [period, setPeriod] = useState("");
  const [openApproveModal, setOpenApproveModal] = React.useState(false);
  const [comment, setComment] = React.useState("");
  const [recommendationText, setRecommendationText] = React.useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [yearFilter, setYearFilter] = useState("");
  const [selectedRecommendation, setSelectedRecommendation] =
    React.useState("");
  const [referenceId, setReferenceId] = useState();
  const [appraiseeId, setAppraiseeId] = useState();
  const handleComment = (e) => {
    setComment(e.target.value);
  };
  const handleApproveModal = (referenceId, appraiseeId) => {
    setReferenceId(referenceId);
    setAppraiseeId(appraiseeId);
    setOpenApproveModal(!openApproveModal);
  };

  const user = GetLocalStorage();
  const getRoleFromDepartment = (departmentName) => {
    switch (departmentName) {
      case "Human Resources":
        return 3;
      case "ED Operations Office":
      case "ED F&I Office":
        return 4;
      case "MD Office":
        return 5;
      default:
        return null;
    }
  };
  const reviewerRole = getRoleFromDepartment(user?.departmentName);
  const { data: recommendation } = useGetHODScoreRecommwndationQuery({
    ApprovalStatus: 0,
    PageNumber: 1,
    PageSize: 1000,
  });
  const { data, isLoading } = useGetManagementAppraisalQuery({
    AppraisalPeriod: period,
    PageNumber: currentPage,
    PageSize: pageSize,
    Status: filter,
    SearchTerm: searchText,
    StartDate: startDate,
    EndDate: endDate,
    ObjectiveId: "",
    ReviewerRole: reviewerRole,
    recommendation: recommendationText,
    DepartmentId: departmentId,
    date: yearFilter,
  });

  const navigate = useNavigate();
  const flattenedKRAs = useMemo(() => {
    if (!data?.responseObject?.pageItems) return [];

    return data.responseObject.pageItems.map((pageItem) => ({
      ...pageItem,
      appraisalPeriod: pageItem.appraisalPeriodDisplay,
      slaDueDate: "N/A", // Since SLA Due Date is not in the new JSON
    }));
  }, [data]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedData = useMemo(() => {
    if (!flattenedKRAs) return [];
    return [...flattenedKRAs].sort((a, b) => {
      if (!sortColumn) return 0;
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];

      // For strings, make case-insensitive
      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [sortColumn, sortDirection, flattenedKRAs]);

  const renderSortIcon = (column) => {
    if (sortColumn !== column) return <FaSort className="ml-1" />;
    return sortDirection === "asc" ? (
      <FaSortUp className="ml-1" />
    ) : (
      <FaSortDown className="ml-1" />
    );
  };

  const handleView = (id) => {
    if (id) {
      navigate(`${id}/view`);
    }
  };
  const filterBy = [
    {
      name: "Draft",
      filter: 0,
    },
    {
      name: "Awaiting LineManager",
      filter: 1,
    },
    {
      name: "Awaiting Peer Review",
      filter: 2,
    },
    {
      name: "Awaiting Upward Review",
      filter: 3,
    },
    {
      name: "Awaiting Cross Review",
      filter: 4,
    },
    {
      name: "Awaiting HOD",
      filter: 5,
    },
    {
      name: "Awaiting HR",
      filter: 6,
    },
    {
      name: "Completed",
      filter: 7,
    },
    {
      name: "Awaiting ED",
      filter: 8,
    },
    {
      name: "Awaiting MD",
      filter: 9,
    },
  ];
  const periodFilter = [
    { name: "Daily", filter: 1 },
    { name: "Weekly", filter: 2 },
    { name: "Monthly", filter: 3 },
    { name: "QUarterly", filter: 4 },
    { name: "BiAnnual", filter: 5 },
    { name: "Yearly", filter: 6 },
  ];

  const [makeComment, { isLoading: commentLoading }] =
    useManagementCommentMutation();
  const initializeComment = async () => {
    if (!comment) {
      toast.error("Please enter a comment");
      return;
    }
    try {
      const response = await makeComment({
        referenceId: referenceId,
        isAccepted: true,
        requiresPip: true,
        comment,
        isOverrideRecommendation: true,
        selectedRecommendation: selectedRecommendation,
        appraiseeId: appraiseeId,
        reviewerRole: reviewerRole,
      }).unwrap();
      if (response?.successful) {
        setOpenApproveModal(false);
        toast.success(response?.statusMessage);
      }
    } catch (error) {
      toast.error(error?.data?.statusMessage ?? "Error approving rating scale");
    }
  };
  const recommendationOptions = recommendation?.result?.map((rec) => ({
    filter: rec.recommendationTitle,
    name: rec.recommendationTitle,
  }));
  const { data: departments } = useGetHrDepartmentsQuery();
  const currentYear = new Date().getFullYear();
  const dates = [
    ...Array.from({ length: currentYear - 1999 + 1 }, (_, i) => ({
      name: (currentYear - i).toString(),
      filter: (currentYear - i).toString(),
    })),
  ];
  const departmentilter = departments?.responseObject?.pageItems?.map(
    (depatment) => ({
      name: depatment.name,
      filter: depatment.id,
    })
  );
  return (
    <PageStyle demoTitle={"APPRASIAL REVIEW MGT"} hasTopPageDemo>
      <form className={DashboardStyle.dashboard_filter}>
        <SearchFilter
          value={searchText}
          text={(searchText) => setSearchText(searchText)}
        />
        <ProDropFilter
          filter={filter}
          setFilter={setFilter}
          name={"Appraisal Workflow Status"}
          filterBy={filterBy}
        />
        <CalendarFilter2
          name="Select Date Range"
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          normalDate
        />
        <ProDropFilter
          filter={period}
          setFilter={setPeriod}
          name={"Period"}
          filterBy={periodFilter}
        />
        <ProDropFilter
          filter={recommendationText}
          setFilter={setRecommendationText}
          name={"Recommendation"}
          filterBy={recommendationOptions}
        />
        <ProDropFilter
          filter={departmentId}
          setFilter={setDepartmentId}
          name={"Deparment"}
          filterBy={departmentilter}
        />
        <ProDropFilter
          filter={yearFilter}
          setFilter={setYearFilter}
          name={"Year"}
          filterBy={dates}
        />
      </form>
      <div className={DashboardStyle.dashboard_table_holder}>
        <Table>
          <thead>
            <tr>
              <th>S/N</th>
              <th
                onClick={() => handleSort("appraiseeName")}
                style={{ cursor: "pointer" }}
              >
                Appraisee Name {renderSortIcon("appraiseeName")}
              </th>
              <th onClick={() => handleSort("department")}>
                Department {renderSortIcon("department")}
              </th>
              <th onClick={() => handleSort("jobTitle")}>
                Job Title {renderSortIcon("jobTitle")}
              </th>
              <th>Appraisal Period</th>
              <th>Score</th>
              <th onClick={() => handleSort("recommendation")}>
                Recommendation {renderSortIcon("recommendation")}
              </th>
              <th>Comment</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={10} className="text-center">
                  Loading...
                </td>
              </tr>
            )}
            {sortedData?.length > 0 &&
              sortedData?.map((kra, index) => (
                <tr key={kra.referenceId || index}>
                  <td>{index + 1}</td>
                  <td>{kra?.appraiseeName}</td>
                  <td>{kra?.department}</td>
                  <td>{kra?.jobTitle}</td>
                  <td>{kra?.appraisalPeriod}</td>
                  <td>{kra?.totalScore}</td>
                  <td>{kra?.recommendation ?? "--"}</td>
                  <td>{kra?.comment ?? "--"}</td>
                  <td>
                    <TableActions
                      hasChildren={true}
                      url={`${kra.referenceId}/view`}
                    >
                      {[
                        {
                          name: "View Details",
                          action: () => handleView(kra.referenceId),
                        },
                        {
                          name: "Comment",
                          action: () =>
                            handleApproveModal(
                              kra.referenceId,
                              kra.appraiseeId
                            ),
                        },
                      ]}
                    </TableActions>
                  </td>
                </tr>
              ))}
            {sortedData?.length === 0 && !isLoading && (
              <tr>
                <td colSpan={10} className="text-center">
                  No KRA Records Found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <ApproveModal
        isOpen={openApproveModal}
        handleClose={handleApproveModal}
        handleDelete={() => initializeComment()}
        isLoading={commentLoading}
        hasComment={true}
        disabled={!comment}
        infoText={"Are you sure you want to submit this comment?"}
        comment={
          <div
            className="flex items-start justify-between gap-3 w-full px-7 py-7"
            style={{ borderBottom: "1px solid #ccc" }}
          >
            <textarea
              cols={"15"}
              rows={"5"}
              placeholder="Enter a comment..."
              onChange={handleComment}
              className="text-xs border border-slate-300 rounded w-full p-2 outline-none focus:ring-0 focus:border-customGreen"
            />
          </div>
        }
        headText={"Comment on Appraisal"}
        extra={
          <select
            name="select"
            id="selectedRecommendation"
            className="text-xs border border-slate-300 my-2 rounded w-full p-2 outline-none focus:ring-0 focus:border-customGreen"
            onChange={(e) => setSelectedRecommendation(e.currentTarget.value)}
          >
            <option value="">Select recommendation</option>
            {recommendation?.result?.map((opt) => (
              <option key={opt.id} value={opt.recommendationTitle}>
                {opt.recommendationTitle}
              </option>
            ))}
          </select>
        }
        btnText={"Submit Comment"}
      />
      <Pagination
        last_page={data?.responseObject?.previousPage}
        present_page={data?.responseObject?.currentPage}
        totalRows={data?.responseObject?.totalCount}
        pageSize={data?.responseObject?.pageSize}
        setPageSize={(page) => setPageSize(page)}
        click={(page) => setCurrentPage(page)}
      />
    </PageStyle>
  );
};

export default ManagementAppraisal;
